import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Paper, Stack, IconButton, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { CVHttpService } from '../../services';
import EducationFormDialog from '../dialogs/EducationFormDialog';

function EducationStep({ cvId }) {
    const [educations, setEducations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingEducation, setEditingEducation] = useState(null);

    useEffect(() => {
        if (cvId) {
            loadEducations();
        }
    }, [cvId]);

    const loadEducations = async () => {
        if (!cvId) return;
        setLoading(true);
        try {
            const service = new (await import('core/services/http')).HttpService();
            const data = await service.get(`/job/cv/education/?cv=${cvId}`, null);
            // Handle both array response and paginated response
            const educationsList = Array.isArray(data) ? data : (data?.results || []);
            setEducations(educationsList);
        } catch (err) {
            console.error('Failed to load educations', err);
            setEducations([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this education entry?')) return;
        try {
            await CVHttpService.deleteEducation(id);
            loadEducations();
        } catch (err) {
            alert('Failed to delete education entry');
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight={600}>
                    Education
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setEditingEducation(null);
                        setOpenDialog(true);
                    }}
                >
                    Add Education
                </Button>
            </Stack>

            {!Array.isArray(educations) || educations.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Typography color="text.secondary">
                        No education entries added yet. Click "Add Education" to get started.
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={2}>
                    {Array.isArray(educations) && educations.map((edu) => (
                        <Paper key={edu.id} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="start">
                                <Box>
                                    <Typography variant="h6" fontWeight={600}>
                                        {edu.level}
                                    </Typography>
                                    {edu.field && (
                                        <Typography variant="body2" color="text.secondary">
                                            {edu.field}
                                        </Typography>
                                    )}
                                    {edu.location && (
                                        <Typography variant="body2" color="text.secondary">
                                            {edu.location}
                                        </Typography>
                                    )}
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setEditingEducation(edu);
                                            setOpenDialog(true);
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDelete(edu.id)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            )}

            <EducationFormDialog
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    setEditingEducation(null);
                }}
                onSave={() => {
                    loadEducations();
                    setOpenDialog(false);
                    setEditingEducation(null);
                }}
                cvId={cvId}
                education={editingEducation}
            />
        </Box>
    );
}

export default EducationStep;

